const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.artizim.com.cn';

export interface ResponseApi<T> {
  code: number;
  msg: string;
  data: T;
}

interface PortfolioListItem {
  id: number;
  title: string;
  subtitle: string;
  slug: string;
  cover_image: string;
  video_url?: string | null;
  sort_order: number;
}

interface PortfolioDetailItem {
  id: number;
  title: string;
  subtitle: string;
  slug: string;
  cover_image: string;
  video_url?: string | null;
  image_list?: string[] | null;
  is_active: boolean;
  sort_order: number;
  view_count: number;
}

export interface PortfolioItem {
  id: number;
  title: string;
  subtitle: string;
  slug: string;
  cover_image: string;
  video_url?: string;
  image_list: string[];
  is_active?: boolean;
  sort_order?: number;
  view_count?: number;
}

export interface AboutInfo {
  id: number;
  name: string;
  avatar: string;
}

export interface ContactInfo {
  id: number;
  name: string;
  avatar: string;
  introduction: string;
  contact_list: string[];
  image_list: string[];
}

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        ...options,
      });

      const result: ResponseApi<T> = await response.json();

      if (!response.ok) {
        throw new Error(result?.msg || `HTTP error! status: ${response.status}`);
      }

      if (result.code !== 200) {
        throw new Error(result.msg || 'API request failed');
      }

      return result.data;
    } catch (error) {
      console.error(`API request failed: ${url}`, error);
      throw error;
    }
  }

  async getPortfolios(): Promise<PortfolioItem[]> {
    const data = await this.request<PortfolioListItem[]>('/api/v1/portfolios/list');

    return [...data]
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((item) => ({
        ...item,
        video_url: item.video_url || '',
        image_list: [],
      }));
  }

  async getPortfolioById(id: number): Promise<PortfolioItem> {
    const data = await this.request<PortfolioDetailItem>(`/api/v1/portfolios/${id}`);

    return {
      ...data,
      video_url: data.video_url || '',
      image_list: Array.isArray(data.image_list) ? data.image_list : [],
    };
  }

  async getPortfolioBySlug(slug: string): Promise<PortfolioItem> {
    const data = await this.request<PortfolioDetailItem>(`/api/v1/portfolios/slug/${slug}`);

    return {
      ...data,
      video_url: data.video_url || '',
      image_list: Array.isArray(data.image_list) ? data.image_list : [],
    };
  }

  async getAbout(): Promise<AboutInfo> {
    return this.request<AboutInfo>('/api/v1/follow-us/about');
  }

  async getContact(): Promise<ContactInfo> {
    const data = await this.request<ContactInfo>('/api/v1/follow-us/contact');

    return {
      ...data,
      contact_list: Array.isArray(data.contact_list)
        ? data.contact_list.map((item) => String(item).trim()).filter(Boolean)
        : [],
      image_list: Array.isArray(data.image_list)
        ? data.image_list.map((item) => String(item).trim()).filter(Boolean)
        : [],
    };
  }
}

export const apiService = new ApiService();
export default apiService;
